import { type AxiosError } from 'axios'
import { z } from 'zod'

import { useTranslation } from '@flecto/translations'
import { type CoreCustomer, type CoreCustomerAddress } from '@flecto/types'

import { type CoreCustomerForm } from '../../types/FormCustomer'

type UseCustomerSchemaOptions = {
  customer?: Partial<CoreCustomer>
}

interface ErrorData {
  errors: { field: string; detail: string }[]
}

export function useCustomerSchema(options?: UseCustomerSchemaOptions) {
  const { t } = useTranslation('saas', { keyPrefix: 'validation' })

  const schema = z
    .object({
      full_name: z
        .string()
        .min(2, { message: t('required') })
        .optional(),
      email: z
        .string()
        .email({ message: t('email') })
        .optional()
        .nullable()
        .or(z.literal('')),
      phone_number: z.string().optional().nullable().or(z.literal('')),
      vat_number: z.string().optional().nullable().or(z.literal('')),
    })
    .superRefine((data, ctx) => {
      if (data.full_name === undefined || data.full_name.length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('required'),
          path: ['full_name'],
        })
      }
      if (!data.email && !data.phone_number && !data.vat_number) {
        ;['email', 'phone_number', 'vat_number'].forEach((field) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'at_least_one',
            path: [field],
          })
        })
      }
    })

  const returnErrorMessage = (error: AxiosError) => {
    const errors: { field: string; errorMessage: string }[] = []
    if (error.response?.status === 422) {
      const errorData = error.response.data as ErrorData
      errorData.errors.forEach((e) => {
        if (e.field === 'customer[full_name]') {
          errors.push({
            field: 'full_name',
            errorMessage: e.detail,
          })
        }
        if (e.field === 'customer[email]') {
          errors.push({
            field: 'email',
            errorMessage: e.detail,
          })
        }
        if (e.field === 'customer[phone_number]') {
          errors.push({
            field: 'phone_number',
            errorMessage: e.detail,
          })
        }
        if (e.field === 'customer[vat_number]') {
          errors.push({
            field: 'vat_number',
            errorMessage: e.detail,
          })
        }
        if (e.field === 'customer[addresses.label]') {
          errors.push({
            field: 'address.label',
            errorMessage: e.detail,
          })
        }
        if (e.field === 'customer[id_document_number]') {
          errors.push({
            field: 'id_document_number',
            errorMessage: e.detail,
          })
        }
        if (e.field === 'customer[addresses.address_postal_code]') {
          errors.push({
            field: 'address.address_postal_code',
            errorMessage: e.detail,
          })
        }
        if (e.field === 'customer[addresses.base]') {
          errors.push({
            field: 'address.address_country_code',
            errorMessage: e.detail,
          })
        }
      })
    }

    return errors
  }

  const address =
    options?.customer?.addresses?.[0] ?? ({} as CoreCustomerAddress)

  const initialValues: Partial<CoreCustomerForm> = {
    ...options?.customer,
    phone_number_country_code:
      options?.customer?.meta?.phone_number_country_code ?? '+351',
    address: {
      id: address.id ?? undefined,
      label: address.label ?? undefined,
      address_street: address.address_street ?? null,
      address_city: address.address_city ?? null,
      address_postal_code: address.address_postal_code ?? null,
      address_region: address.address_region ?? null,
      address_country_code: address.address_country_code ?? null,
      address_country_name: address.address_country_name ?? null,
      latitude: address.latitude ?? null,
      longitude: address.longitude ?? null,
    },
  }

  const verifyIdentification = (docNumber: string, docType: string) => {
      if (!docType) return true

    const verify = options?.customer?.id_verified_at
      ? String(options?.customer?.id_document_number) === String(docNumber) &&
        String(options?.customer?.id_document_type) === String(docType)
      : true

    return verify
  }

  return { schema, initialValues, returnErrorMessage, verifyIdentification }
}
